import axios from "axios";

export const getShopAPIURL = local => {
  const { origin } = window.location;
  const environmentCode = window.Constants.SITE_ID;
  if (origin && environmentCode) {
    return `${
      local || origin.indexOf("store-dev.ubi.com") > -1 ? "https://store.ubi.com" : origin
    }/s/${environmentCode}/dw/shop/v19_8/`;
  }
  return false;
};

export const splitIntoParts = (arr, limit) => {
  const length = arr.length;
  let index = 0;
  const results = [];

  while (index < length) {
    results.push(arr.slice(index, index + limit));
    index += limit;
  }

  return results;
};

export const buildProductURL = (url, productIds, currency, clientId) =>
  `${url}products/(${productIds.join(
    ","
  )})?expand=images,variations,prices,promotions,availability&currency=${currency}&client_id=${clientId}`;

const fixVariantPrice = (variant) => {
  if(variant.product_promotions === undefined){
    variant.displayPrice = {
      regular: variant.price,
      promotionalPrice: variant.price,
      percentOff: 0
    };
    variant.isInPromo = false;
    return variant;
  }

  const pricePromotion = variant.product_promotions.filter(promo =>
    promo.hasOwnProperty('promotional_price')
  );

  if (pricePromotion.length > 0) {
    pricePromotion.sort((a, b) => (a.promotional_price > b.promotional_price) ? 1 : -1);
    variant.displayPrice = {
      regular: variant.price,
      promotionalPrice: pricePromotion[0].promotional_price
    };
    variant.isInPromo = true;
  } else {
    variant.displayPrice = {
      regular: variant.price,
      promotionalPrice: variant.price
    };
    variant.isInPromo = false;
  }

  return variant;
};

const sortVariantsByPrice = (variants, priceKey) => {
  return variants.sort((a, b) => {
    if (a.displayPrice[priceKey] < b.displayPrice[priceKey]) {
      return -1;
    }

    if (a.displayPrice[priceKey] > b.displayPrice[priceKey]) {
      return 1;
    }

    return 0;
  });
};

const formatProduct = (product, displayPrice) => (
  {
    id: product.id,
    masterId: product.id,
    isInPromo: product.isInPromo,
    displayPrice: displayPrice,
    name: {
      fullName: product.name,
      shortName: product.c_productShortNameString,
      edition: product.c_productEditionString
    },
    originalData: product
  }
);

export const getProducts = (productIds, currency, clientId, forceFetchVariants) => {
  const buggedCurrencies = ["NOK", "DKK", "PLN", "SEK", "IDR", "MYR", "PHP"];

  return new Promise((resolve, reject) => {
    const productSets = splitIntoParts(productIds, 40);

    const productCalls = [];
    productSets.forEach(set => {
      const url = buildProductURL(getShopAPIURL(process.env.NODE_ENV === "development"), set, currency, clientId);
      productCalls.push(axios.get(url));
    });

    Promise.all(productCalls).then((responses) => {
      const products = responses.map(response => response.data.data).flat();

      if (buggedCurrencies.indexOf(currency) !== -1 || forceFetchVariants) {
        const variants = products.map((product) => product.variants.map((variant) => variant.product_id));

        const variantsSets = splitIntoParts(variants, 40);
        const variantsCalls = [];
        variantsSets.forEach(set => {
          const url = buildProductURL(getShopAPIURL(process.env.NODE_ENV === "development"), set, currency, clientId);
          variantsCalls.push(axios.get(url));
        });

        Promise.all(variantsCalls).then((variantResponses) => {
          const variantsProducts = variantResponses.map(response => response.data.data).flat();
          variantsProducts.map(variant => fixVariantPrice(variant));
          products.map(product => {
            product.variants = variantsProducts.filter(variant => variant.master.master_id === product.id);
            product.orderableVariants = product.variants.filter(variant => variant.inventory.orderable);
            product.isInPromo = product.orderableVariants.filter(variant => variant.price.promotional_price !== null).length > 0;

            const priceKey = product.isInPromo ? 'promotional_price' : 'regular';
            product.variants = sortVariantsByPrice(product.variants, priceKey);

            if (product.variants.length > 0 && product.c_display_price) {
              const productDisplayPrice = JSON.parse(product.c_display_price);
              product.displayPrice = {
                regular: productDisplayPrice.regular,
                promotionalPrice: productDisplayPrice.promo_price,
                percentOff: productDisplayPrice.percent_off > 0 ? parseFloat((1 - productDisplayPrice.percent_off).toFixed(2)) : 0
              };
              if (typeof product.displayPrice.regular !== "number" && typeof product.displayPrice.regular !== "string") {
                product.displayPrice.regular = product.price;
              }
              if (typeof product.displayPrice.promotionalPrice !== "number" && typeof product.displayPrice.promotionalPrice !== "string") {
                product.displayPrice.promotionalPrice = product.price;
              }
            } else if (product.variants.length > 0) {
              product.displayPrice = product.variants[0].displayPrice;
              product.hasSameVariantPrices = product.variants.every((item, i, arr) => item.displayPrice[priceKey] === arr[0].displayPrice[priceKey])
            } else {
              product = fixVariantPrice(product);
            }
            return product;
          });
          const productsToReturn = products.filter(product => {
            return !!product;
          });
          resolve(productsToReturn.map(product => {
            let displayPrice = product.displayPrice;
            if (displayPrice) {
              const percentOff = displayPrice.promotionalPrice / displayPrice.regular;
              displayPrice.percentOff = percentOff < 1 ? parseFloat((1 - percentOff).toFixed(2)) : 0;
            } else {
              displayPrice = {
                regular: product.price,
                promotionalPrice: product.price,
                percentOff: 0
              };
            }
            return formatProduct(product, displayPrice);
          }));
        });
      } else {
        const productsToReturn = products.filter(product => {
          return !!product;
        });
        resolve(productsToReturn.map(product => {
          const displayPrice = {
            regular: product.price,
            promotionalPrice: product.price,
            percentOff: 0
          };
          if (product.c_display_price) {
            const productDisplayPrice = JSON.parse(product.c_display_price);
            displayPrice.regular = productDisplayPrice.regular;
            displayPrice.promotionalPrice = productDisplayPrice.promo_price;
            displayPrice.percentOff = productDisplayPrice.percent_off > 0 ? parseFloat((1 - productDisplayPrice.percent_off).toFixed(2)) : 0;
          } else if (product.product_promotions) {
            const sortedPromotions = product.product_promotions.filter(promo => promo.promotional_price).sort((a, b) => (a.promotional_price > b.promotional_price) ? 1 : -1);
            if (sortedPromotions.length > 0 && sortedPromotions[0].promotional_price && sortedPromotions[0].promotional_price < displayPrice.regular) {
              displayPrice.promotionalPrice = sortedPromotions[0].promotional_price;
              const percentOff = displayPrice.promotionalPrice / displayPrice.regular;
              displayPrice.percentOff = parseFloat((1 - percentOff).toFixed(2));
            }
          }
          return formatProduct(product, displayPrice);
        }));
      }
    })
  })
};